import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { forwardRef, useState, type ReactNode } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
  useMonkeysDirection,
  useMonkeysLocaleMessages,
} from '../../provider/context';
import { useBaseOverlayPortal } from './internal/overlayPortal';
import type {
  BaseDropdownMenuItem,
  BaseDropdownMenuPlacement,
  BaseDropdownMenuProps,
} from './types';
import {
  getBaseButtonToneClassName,
  resolveBaseAppearance,
} from './theme';
import { cn, hasRenderableNode } from './utils';

function validateBoolean(value: unknown, name: string) {
  if (typeof value !== 'boolean') {
    throw new TypeError(`BaseDropdownMenu ${name} must be a boolean.`);
  }
}

function validatePlacement(value: unknown) {
  if (
    value !== 'bottom-start' &&
    value !== 'bottom-end' &&
    value !== 'top-start' &&
    value !== 'top-end'
  ) {
    throw new TypeError(
      'BaseDropdownMenu placement must be "bottom-start", "bottom-end", "top-start", or "top-end".',
    );
  }
}

function validateDirection(value: unknown) {
  if (value !== undefined && value !== 'ltr' && value !== 'rtl') {
    throw new TypeError('BaseDropdownMenu dir must be "ltr" or "rtl".');
  }
}

function validateItems(items: unknown): asserts items is BaseDropdownMenuItem[] {
  if (!Array.isArray(items)) {
    throw new TypeError('BaseDropdownMenu items must be an array.');
  }

  const values = new Set<string>();
  for (const [index, item] of items.entries()) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      throw new TypeError(`BaseDropdownMenu items[${index}] must be an object.`);
    }
    if (typeof item.value !== 'string' || !item.value.trim()) {
      throw new TypeError(
        `BaseDropdownMenu items[${index}].value must be a non-empty string.`,
      );
    }
    if (values.has(item.value)) {
      throw new TypeError(
        `BaseDropdownMenu item value "${item.value}" must be unique.`,
      );
    }
    values.add(item.value);
    for (const name of ['disabled', 'danger', 'separatorBefore'] as const) {
      const value = item[name];
      if (value !== undefined) {
        validateBoolean(value, `items[${index}].${name}`);
      }
    }
    if (item.onSelect !== undefined && typeof item.onSelect !== 'function') {
      throw new TypeError(
        `BaseDropdownMenu items[${index}].onSelect must be a function.`,
      );
    }
  }
}

function resolvePlacement(placement: BaseDropdownMenuPlacement) {
  const [side, align] = placement.split('-') as [
    'bottom' | 'top',
    'end' | 'start',
  ];
  return { side, align };
}

function renderTrigger({
  trigger,
  triggerLabel,
  triggerIcon,
  disabled,
  theme,
}: Pick<
  BaseDropdownMenuProps,
  'disabled' | 'trigger' | 'triggerIcon' | 'triggerLabel'
> & {
  triggerLabel: ReactNode;
  theme: ReturnType<typeof resolveBaseAppearance>;
}) {
  return hasRenderableNode(trigger) ? (
    <span
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
    >
      {trigger}
    </span>
  ) : (
    <button
      type="button"
      disabled={disabled}
      aria-label={
        typeof triggerLabel === 'string' && triggerLabel.trim()
          ? triggerLabel
          : undefined
      }
      className={cn(
        theme.slots.buttonBase,
        getBaseButtonToneClassName(theme),
        disabled && theme.slots.buttonDisabled,
      )}
      style={{
        ...theme.styles.button,
        ...(disabled ? theme.styles.buttonDisabled : undefined),
      }}
    >
      {hasRenderableNode(triggerIcon) ? (
        <span data-slot="icon" className={theme.slots.buttonIcon}>
          {triggerIcon}
        </span>
      ) : null}
      {hasRenderableNode(triggerLabel) ? (
        <span data-slot="label" className={theme.slots.buttonLabel}>
          {triggerLabel}
        </span>
      ) : null}
    </button>
  );
}

export const BaseDropdownMenu = forwardRef<
  HTMLDivElement,
  BaseDropdownMenuProps
>(function BaseDropdownMenu(
  {
    items = [],
    label,
    trigger,
    triggerLabel,
    triggerIcon,
    disabled = false,
    open,
    defaultOpen = false,
    placement = 'bottom-start',
    closeOnSelect = true,
    portal = false,
    dir,
    appearance,
    className,
    classNames,
    style,
    children,
    onOpenChange,
    onSelect,
    ...menuProps
  },
  ref,
) {
  if (open !== undefined) validateBoolean(open, 'open');
  validateBoolean(defaultOpen, 'defaultOpen');
  validateBoolean(disabled, 'disabled');
  validateBoolean(closeOnSelect, 'closeOnSelect');
  validateBoolean(portal, 'portal');
  validatePlacement(placement);
  validateDirection(dir);
  validateItems(items);

  const inheritedDirection = useMonkeysDirection();
  const resolvedDirection = dir ?? inheritedDirection;
  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const messages = useMonkeysLocaleMessages().dropdownMenu;
  const theme = resolveBaseAppearance(resolvedAppearance);
  const overlayPortal = useBaseOverlayPortal({ disabled: !portal });
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : uncontrolledOpen;
  const resolvedTriggerLabel =
    triggerLabel === undefined ? messages.actions : triggerLabel;
  const portalState =
    portal && overlayPortal.shouldPortal ? 'portaled' : 'inline';
  const { side, align } = resolvePlacement(placement);

  if (
    triggerLabel === undefined &&
    (typeof messages.actions !== 'string' || !messages.actions.trim())
  ) {
    throw new TypeError(
      'BaseDropdownMenu locale actions message must be a non-empty string.',
    );
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (disabled) return;
    if (!isControlled) {
      setUncontrolledOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  const menuContent = (
    <DropdownMenu.Content
      {...componentAttributes}
      side={side}
      align={align}
      sideOffset={6}
      collisionPadding={8}
      data-monkeys-component="dropdown-menu"
      data-slot="content"
      data-placement={placement}
      data-portal={portalState}
      data-tone="default"
      data-disabled={disabled || undefined}
      className={cn(theme.slots.dropdownMenu, classNames?.menu)}
      style={theme.styles.dropdownMenu}
    >
      {hasRenderableNode(label) ? (
        <DropdownMenu.Label
          {...componentAttributes}
          data-monkeys-component="dropdown-menu"
          data-slot="label"
          data-state="static"
          className={cn(theme.slots.dropdownLabel, classNames?.label)}
        >
          {label}
        </DropdownMenu.Label>
      ) : null}
      {items.map((item) => {
        const itemDisabled = item.disabled ?? false;
        const tone = item.danger ? 'danger' : 'default';

        return (
          <div
            key={item.value}
            data-monkeys-component="dropdown-menu"
            data-slot="item-wrapper"
          >
            {item.separatorBefore ? (
              <DropdownMenu.Separator
                {...componentAttributes}
                data-monkeys-component="dropdown-menu"
                data-slot="separator"
                data-state="static"
                className={cn(
                  theme.slots.dropdownSeparator,
                  classNames?.separator,
                )}
              />
            ) : null}
            <DropdownMenu.Item
              asChild
              disabled={itemDisabled}
              onSelect={(event) => {
                item.onSelect?.(item);
                onSelect?.(item);
                if (!closeOnSelect) {
                  event.preventDefault();
                }
              }}
            >
              <button
                {...componentAttributes}
                type="button"
                disabled={itemDisabled}
                data-monkeys-component="dropdown-menu"
                data-slot="item"
                data-state={itemDisabled ? 'disabled' : 'enabled'}
                data-value={item.value}
                data-placement={placement}
                data-portal={portalState}
                data-tone={tone}
                data-disabled={itemDisabled || undefined}
                className={cn(
                  theme.slots.dropdownItem,
                  item.danger && theme.slots.dropdownItemDanger,
                  itemDisabled && theme.slots.dropdownItemDisabled,
                  classNames?.item,
                )}
                style={theme.styles.dropdownItem}
              >
                {hasRenderableNode(item.icon) ? (
                  <span
                    data-slot="item-icon"
                    className={cn(
                      theme.slots.dropdownItemIcon,
                      classNames?.itemIcon,
                    )}
                  >
                    {item.icon}
                  </span>
                ) : null}
                <span
                  data-slot="item-content"
                  className={cn(
                    theme.slots.dropdownItemContent,
                    classNames?.itemContent,
                  )}
                >
                  <span
                    data-slot="item-label"
                    className={cn(
                      theme.slots.dropdownItemLabel,
                      classNames?.itemLabel,
                    )}
                  >
                    {item.label}
                  </span>
                  {hasRenderableNode(item.description) ? (
                    <span
                      data-slot="item-description"
                      className={cn(
                        theme.slots.dropdownItemDescription,
                        classNames?.itemDescription,
                      )}
                    >
                      {item.description}
                    </span>
                  ) : null}
                </span>
                {hasRenderableNode(item.shortcut) ? (
                  <span
                    data-slot="item-shortcut"
                    className={cn(
                      theme.slots.dropdownItemShortcut,
                      classNames?.itemShortcut,
                    )}
                  >
                    {item.shortcut}
                  </span>
                ) : null}
              </button>
            </DropdownMenu.Item>
          </div>
        );
      })}
      {children}
    </DropdownMenu.Content>
  );

  return (
    <div
      {...componentAttributes}
      {...menuProps}
      ref={ref}
      dir={resolvedDirection}
      data-monkeys-component="dropdown-menu"
      data-slot="root"
      data-state={isOpen ? 'open' : 'closed'}
      data-placement={placement}
      data-portal={portalState}
      data-tone="default"
      data-disabled={disabled || undefined}
      className={cn(theme.slots.dropdownRoot, classNames?.root, className)}
      style={style}
    >
      <DropdownMenu.Root
        open={isOpen}
        onOpenChange={handleOpenChange}
        dir={resolvedDirection}
        modal={false}
      >
        <DropdownMenu.Trigger
          {...componentAttributes}
          asChild
          disabled={disabled}
          data-monkeys-component="dropdown-menu"
          data-slot="trigger"
          data-placement={placement}
          data-portal={portalState}
          data-tone="default"
          data-disabled={disabled || undefined}
          className={cn(
            theme.slots.dropdownTrigger,
            disabled && theme.slots.dropdownTriggerDisabled,
            classNames?.trigger,
          )}
        >
          {renderTrigger({
            trigger,
            triggerLabel: resolvedTriggerLabel,
            triggerIcon,
            disabled,
            theme,
          })}
        </DropdownMenu.Trigger>
        {portal && overlayPortal.shouldPortal ? (
          <DropdownMenu.Portal {...overlayPortal.radixPortalProps}>
            {menuContent}
          </DropdownMenu.Portal>
        ) : (
          menuContent
        )}
      </DropdownMenu.Root>
    </div>
  );
});
